/**
 * API client.
 *
 * One `request` function every hook goes through, so auth (cookies), error
 * normalization and the response envelope are handled in exactly one place.
 * Endpoints below mirror the route files in apps/api/src/routes.
 */

const BASE_URL = import.meta.env['VITE_API_URL'] ?? '/api/v1';

// ── Active project ──────────────────────────────────────────────────────────
// The selection is client state (localStorage) sent as X-Project-Id on every
// call; the server validates membership on each request and falls back to the
// user's first project when the header is absent.

const ACTIVE_PROJECT_KEY = 'difference.active_project';

export function getActiveProjectId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_PROJECT_KEY);
  } catch {
    return null;
  }
}

export function setActiveProjectId(projectId: string | null): void {
  try {
    if (projectId) localStorage.setItem(ACTIVE_PROJECT_KEY, projectId);
    else localStorage.removeItem(ACTIVE_PROJECT_KEY);
  } catch {
    // Storage unavailable (private mode) — the server-side fallback covers it.
  }
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(message: string, status: number, code: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the user simply isn't signed in — the app redirects rather than alerting. */
  get isUnauthenticated() {
    return this.status === 401;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${endpoint}`, {
      ...rest,
      // Session auth is cookie-based, so credentials must always be included.
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(getActiveProjectId() && { 'X-Project-Id': getActiveProjectId()! }),
        ...headers,
      },
      ...(body !== undefined && { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiClientError('Could not reach the API', 0, 'NETWORK_ERROR');
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = payload?.error ?? {};
    throw new ApiClientError(
      error.message ?? 'Request failed',
      response.status,
      error.code ?? 'UNKNOWN',
      error.details
    );
  }

  // Unwrap the `{ success, data }` envelope so callers deal in domain objects.
  return (payload?.data ?? payload) as T;
}

// ── Types ───────────────────────────────────────────────────────────────────

export interface Account {
  id: string;
  email: string;
  name: string | null;
  company: string | null;
  role: string;
  status: string;
  email_verified: boolean;
  totp_enabled: boolean;
  created_at: string;
}

export interface ApiKey {
  id: string;
  name: string | null;
  mode: 'test' | 'live';
  status: string;
  public_key: string;
  secret_masked: string;
  scopes: string[];
  last_used_at: string | null;
  request_count: number;
  created_at: string;
}

export interface CreatedApiKey extends Omit<ApiKey, 'secret_masked' | 'status' | 'last_used_at' | 'request_count'> {
  secret_key: string;
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  mode: 'test' | 'live';
  status: string;
  description: string | null;
  events: string[];
  secret_masked: string;
  consecutive_failures: number;
  last_delivery_at: string | null;
  created_at: string;
}

export interface Overview {
  active_api_keys: number;
  webhook_endpoints: number;
  requests_24h: number;
  failed_requests_24h: number;
  pending_deliveries: number;
  error_rate_24h: number;
}

export interface RequestLogEntry {
  id: string;
  requestId: string;
  method: string;
  path: string;
  statusCode: number;
  durationMs: number;
  errorCode: string | null;
  createdAt: string;
}

export interface Paginated<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
    has_more: boolean;
  };
}

export type MembershipRole = 'owner' | 'admin' | 'treasurer' | 'developer' | 'operator';

export interface OrganizationInfo {
  id: string;
  name: string;
  role: MembershipRole;
  created_at: string;
}

export interface MemberInfo {
  user_id: string;
  email: string;
  name: string | null;
  role: MembershipRole;
  joined_at: string;
}

export interface InviteInfo {
  id: string;
  email: string;
  role: MembershipRole;
  expires_at: string;
  created_at: string;
}

export interface ProjectInfo {
  id: string;
  name: string;
  organization_id: string;
  organization_name: string;
  role: MembershipRole;
  created_at: string;
}

// ── Endpoints ───────────────────────────────────────────────────────────────

export const auth = {
  register: (body: { email: string; password: string; name?: string; company?: string }) =>
    request<Account>('/auth/register', { method: 'POST', body }),

  login: (email: string, password: string) =>
    request<Account>('/auth/login', { method: 'POST', body: { email, password } }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  me: () => request<Account>('/auth/me'),

  changePassword: (current_password: string, new_password: string) =>
    request<void>('/auth/change-password', {
      method: 'POST',
      body: { current_password, new_password },
    }),

  forgotPassword: (email: string) =>
    request<void>('/auth/forgot-password', { method: 'POST', body: { email } }),

  verifyEmail: (token: string) =>
    request<Account>('/auth/verify-email', { method: 'POST', body: { token } }),

  resetPassword: (token: string, new_password: string) =>
    request<void>('/auth/reset-password', { method: 'POST', body: { token, new_password } }),

  resendVerification: () => request<void>('/auth/resend-verification', { method: 'POST' }),
};

export const orgs = {
  list: () => request<OrganizationInfo[]>('/orgs'),

  create: (name: string) => request<OrganizationInfo>('/orgs', { method: 'POST', body: { name } }),

  rename: (id: string, name: string) =>
    request<OrganizationInfo>(`/orgs/${id}`, { method: 'PATCH', body: { name } }),

  members: (id: string) => request<MemberInfo[]>(`/orgs/${id}/members`),

  changeRole: (id: string, userId: string, role: MembershipRole) =>
    request<MemberInfo[]>(`/orgs/${id}/members/${userId}`, { method: 'PATCH', body: { role } }),

  removeMember: (id: string, userId: string) =>
    request<void>(`/orgs/${id}/members/${userId}`, { method: 'DELETE' }),

  invites: (id: string) => request<InviteInfo[]>(`/orgs/${id}/invites`),

  invite: (id: string, email: string, role: MembershipRole) =>
    request<{ invite: InviteInfo | null; attached: boolean }>(`/orgs/${id}/invites`, {
      method: 'POST',
      body: { email, role },
    }),

  revokeInvite: (id: string, inviteId: string) =>
    request<void>(`/orgs/${id}/invites/${inviteId}`, { method: 'DELETE' }),
};

export const projects = {
  list: () => request<ProjectInfo[]>('/projects'),

  current: () => request<ProjectInfo | null>('/projects/current'),

  create: (organization_id: string, name: string) =>
    request<ProjectInfo>('/projects', { method: 'POST', body: { organization_id, name } }),

  rename: (id: string, name: string) =>
    request<ProjectInfo>(`/projects/${id}`, { method: 'PATCH', body: { name } }),
};

export interface StuckOrderInfo {
  id: string;
  kind: 'onramp' | 'offramp';
  status: string;
  received: string;
  destination: string;
  created_at: string;
  updated_at: string;
}

export interface Analytics {
  onramp: {
    completed: number; failed: number; expired: number; pending: number;
    success_rate: number; ngn_in: string; usdc_out: string;
  };
  offramp: {
    completed: number; failed: number; expired: number; pending: number;
    success_rate: number; usdc_in: string; ngn_out: string;
  };
  onramp_stripe: {
    completed: number; failed: number; pending: number; held: number;
    success_rate: number; usd_in: string; usdc_out: string;
  };
  totals: { completed: number; pending: number; success_rate: number };
  stuck: {
    onramp_count: number; offramp_count: number;
    ngn_at_risk: string; usdc_at_risk: string; orders: StuckOrderInfo[];
  };
  fees: { payout_fee_ngn: string; dextopus_cut_usdc: string };
  daily: { date: string; onramp_ngn: string; offramp_ngn: string }[];
}

export const analytics = {
  get: () => request<Analytics>('/dashboard/analytics'),
};

export interface CounterpartySummary {
  address: string;
  direction: 'sent' | 'received' | 'both';
  interaction_count: number;
  total_usdc_sent: string;
  total_usdc_received: string;
  first_seen: string;
  last_seen: string;
}

export interface CounterpartyInteraction {
  order_id: string;
  type: 'onramp' | 'offramp';
  status: string;
  amount_usdc: string | null;
  amount_ngn: string | null;
  rate: string;
  tx_hash: string | null;
  destination_chain_id: number;
  created_at: string;
}

export interface CounterpartyDetail {
  address: string;
  direction: 'sent' | 'received' | 'both';
  interaction_count: number;
  total_usdc_sent: string;
  total_usdc_received: string;
  first_seen: string | null;
  last_seen: string | null;
  interactions: CounterpartyInteraction[];
}

export const counterparties = {
  list: () => request<CounterpartySummary[]>('/dashboard/counterparties'),
  get: (address: string) =>
    request<CounterpartyDetail>(`/dashboard/counterparties/${encodeURIComponent(address)}`),
};

export const apiKeys = {
  list: (mode?: 'test' | 'live') =>
    request<ApiKey[]>(`/api-keys${mode ? `?mode=${mode}` : ''}`),

  create: (body: { name?: string; mode: 'test' | 'live'; scopes?: string[] }) =>
    request<CreatedApiKey>('/api-keys', { method: 'POST', body }),

  revoke: (id: string) => request<void>(`/api-keys/${id}`, { method: 'DELETE' }),
};

export const webhooks = {
  list: () => request<WebhookEndpoint[]>('/webhooks'),

  create: (body: { url: string; mode: 'test' | 'live'; description?: string; events?: string[] }) =>
    request<WebhookEndpoint & { secret: string }>('/webhooks', { method: 'POST', body }),

  update: (id: string, body: Partial<{ url: string; description: string; status: 'active' | 'paused' }>) =>
    request<WebhookEndpoint>(`/webhooks/${id}`, { method: 'PATCH', body }),

  remove: (id: string) => request<void>(`/webhooks/${id}`, { method: 'DELETE' }),

  deliveries: (id: string, page = 1) =>
    request<Paginated<Record<string, unknown>>>(`/webhooks/${id}/deliveries?page=${page}`),
};

export const dashboard = {
  overview: () => request<Overview>('/dashboard/overview'),

  requests: (page = 1) => request<Paginated<RequestLogEntry>>(`/dashboard/requests?page=${page}`),

  auditLogs: (page = 1) =>
    request<Paginated<Record<string, unknown>>>(`/dashboard/audit-logs?page=${page}`),
};

// ── Ramp domain ─────────────────────────────────────────────────────────────

export interface WalletInfo {
  id: string;
  address: string;
  chain_id: number;
  asset: string;
  balance: string;
  balance_units: string;
  created_at: string;
}

export interface DepositAddressInfo {
  id: string;
  origin_chain_id: number;
  origin_asset: string;
  deposit_address: string;
  status: string;
  created_at: string;
}

export interface DepositInfo {
  id: string;
  origin_chain_id: number;
  origin_asset: string;
  origin_amount: string | null;
  amount: string | null;
  offramp_order_id: string | null;
  status: string;
  created_at: string;
}

export interface TransferInfo {
  id: string;
  destination_address: string;
  destination_chain_id: number;
  amount: string;
  method: string;
  tx_hash: string | null;
  onramp_order_id: string | null;
  status: string;
  error: string | null;
  created_at: string;
}

export interface FiatAccountInfo {
  id: string;
  account_number: string;
  account_name: string;
  bank_name: string;
  currency: string;
  balance: string;
  deposit_account: {
    account_number: string;
    account_name: string;
    bank_name: string;
  } | null;
  created_at: string;
}

export interface CollectionInfo {
  id: string;
  amount: string;
  payer_name: string | null;
  payer_bank_name: string | null;
  created_at: string;
}

export interface PayoutInfo {
  id: string;
  reference: string;
  amount: string;
  destination_account: string;
  destination_bank_name: string | null;
  recipient_name: string | null;
  offramp_order_id: string | null;
  status: string;
  error: string | null;
  created_at: string;
}

export interface RatesInfo {
  onramp_rate: string | null;
  offramp_rate: string | null;
  cost_rate: string | null;
  updated_at: string | null;
}

export interface OfframpInfo {
  id: string;
  integrator_ref: string | null;
  rate: string;
  origin_chain_id: number;
  origin_asset: string;
  deposit_address: string | null;
  destination_account: string;
  recipient_name: string | null;
  amount_usdc: string | null;
  amount_ngn: string | null;
  fee_ngn: string | null;
  settlement_tx_hash: string | null;
  origin_tx_hash: string | null;
  status: string;
  error: string | null;
  expires_at: string;
  created_at: string;
}

export interface OnrampInfo {
  id: string;
  integrator_ref: string | null;
  rate: string;
  expected_ngn: string | null;
  payment_account: { account_number: string; account_name: string; bank_name: string } | null;
  destination_address: string;
  destination_chain_id: number;
  amount_ngn: string | null;
  amount_usdc: string | null;
  delivery_tx_hash: string | null;
  status: string;
  error: string | null;
  expires_at: string;
  created_at: string;
}

export interface BankInfo {
  uuid: string;
  name: string;
}

export const wallet = {
  create: () => request<WalletInfo>('/wallet', { method: 'POST' }),
  get: () => request<WalletInfo>('/wallet'),
  createDepositAddress: (origin_chain_id: number, origin_asset: string) =>
    request<DepositAddressInfo>('/wallet/deposit-addresses', {
      method: 'POST',
      body: { origin_chain_id, origin_asset },
    }),
  depositAddresses: () => request<DepositAddressInfo[]>('/wallet/deposit-addresses'),
  deposits: (page = 1) => request<Paginated<DepositInfo>>(`/wallet/deposits?page=${page}`),
  transfer: (body: {
    destination_address: string;
    amount: string;
    destination_chain_id?: number;
    destination_asset?: string;
  }) => request<TransferInfo>('/wallet/transfers', { method: 'POST', body }),
  transfers: (page = 1) => request<Paginated<TransferInfo>>(`/wallet/transfers?page=${page}`),
};

export const fiat = {
  create: () => request<FiatAccountInfo>('/fiat-account', { method: 'POST' }),
  get: () => request<FiatAccountInfo>('/fiat-account'),
  collections: (page = 1) =>
    request<Paginated<CollectionInfo>>(`/fiat-account/collections?page=${page}`),
  payout: (body: {
    amount: string;
    destination_account: string;
    destination_bank_uuid: string;
    narration?: string;
  }) => request<PayoutInfo>('/payouts', { method: 'POST', body }),
  payouts: (page = 1) => request<Paginated<PayoutInfo>>(`/payouts?page=${page}`),
  banks: () => request<BankInfo[]>('/banks'),
  resolve: (account_number: string, bank_uuid: string) =>
    request<{ account_name: string }>('/banks/resolve', {
      method: 'POST',
      body: { account_number, bank_uuid },
    }),
};

export const rates = {
  get: () => request<RatesInfo>('/rates'),
  update: (body: { onramp_rate?: string; offramp_rate?: string; cost_rate?: string }) =>
    request<RatesInfo>('/rates', { method: 'PATCH', body }),
};

export interface Profit {
  cost_rate: string;
  cost_rate_set: boolean;
  total_profit_ngn: string;
  total_profit_usd: string;
  onramp: {
    orders: number;
    ngn_in: string;
    usdc_out: string;
    cost_ngn: string;
    profit_ngn: string;
  };
  offramp: {
    orders: number;
    usdc_in: string;
    ngn_out: string;
    value_ngn: string;
    profit_ngn: string;
  };
  onramp_stripe: {
    orders: number;
    usd_in: string;
    usdc_out: string;
    value_usd: string;
    profit_usd: string;
  };
}

export const profit = {
  get: () => request<Profit>('/dashboard/profit'),
};

export interface StripeConnectStatus {
  connected: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
}

export const stripeConnect = {
  status: () => request<StripeConnectStatus>('/dashboard/stripe/connect/status'),
  refresh: () => request<StripeConnectStatus>('/dashboard/stripe/connect/refresh', { method: 'POST' }),
  onboard: () => request<{ url: string }>('/dashboard/stripe/connect/onboard', { method: 'POST' }),
};

export interface UsdBalance {
  earned_usd: string;
  reserved_usd: string;
  available_usd: string;
}

export interface UsdPayout {
  id: string;
  amount_usd: string;
  status: 'requested' | 'completed' | 'rejected';
  note: string | null;
  admin_note: string | null;
  created_at: string;
  completed_at: string | null;
}

export const usd = {
  balance: () => request<UsdBalance>('/dashboard/stripe/balance'),
  payouts: (page = 1) => request<Paginated<UsdPayout>>(`/dashboard/stripe/payouts?page=${page}`),
  requestPayout: (amount: string, note?: string) =>
    request<UsdPayout>('/dashboard/stripe/payouts', {
      method: 'POST',
      body: { amount, ...(note && { note }) },
    }),
};

export interface OwedOfframp {
  order_id: string;
  recipient_name: string | null;
  destination_account: string;
  settled_usdc: string | null;
  owed_ngn: string | null;
  finding: string | null;
  order_status: string;
  already_paid: boolean;
  deposit_address: string | null;
  origin_tx_hash: string | null;
  settlement_tx_hash: string | null;
  created_at: string;
}

export const owedOfframps = {
  list: () => request<OwedOfframp[]>('/dashboard/owed-offramps'),
  pay: (orderId: string) =>
    request<{ paid: boolean; owed_ngn: string }>(`/dashboard/owed-offramps/${orderId}/pay`, {
      method: 'POST',
    }),
};

export interface FailedOnramp {
  order_id: string;
  integrator_ref: string | null;
  destination_address: string;
  destination_chain_id: number;
  destination_asset: string;
  amount_ngn: string | null;
  amount_usdc: string | null;
  rate: string | null;
  error: string | null;
  delivery_attempts: number;
  wallet_usdc: string;
  has_liquidity: boolean;
  created_at: string;
}

export const failedOnramps = {
  list: () => request<FailedOnramp[]>('/dashboard/failed-onramps'),
  fulfill: (orderId: string) =>
    request<{ fulfilled: boolean; status: string; amount_usdc: string | null; tx_hash: string | null }>(
      `/dashboard/failed-onramps/${orderId}/fulfill`,
      { method: 'POST' }
    ),
};

export interface OrderFilters {
  status?: string;
  q?: string;
  from?: string;
  to?: string;
  min_ngn?: string;
  max_ngn?: string;
}

function orderQuery(page: number, f: OrderFilters = {}): string {
  const p = new URLSearchParams({ page: String(page) });
  for (const [k, v] of Object.entries(f)) if (v) p.set(k, v);
  return p.toString();
}

export const offramps = {
  list: (page = 1, filters: OrderFilters = {}) =>
    request<Paginated<OfframpInfo>>(`/offramps?${orderQuery(page, filters)}`),
  get: (id: string) => request<OfframpInfo>(`/offramps/${id}`),
};

export interface LedgerEntry {
  id: string;
  currency: 'USDC' | 'NGN';
  amount: string;
  balance_after: string;
  type: string;
  reference: string | null;
  created_at: string;
}

export const ledger = {
  list: (page = 1, currency?: 'USDC' | 'NGN') =>
    request<Paginated<LedgerEntry>>(
      `/ledger?page=${page}${currency ? `&currency=${currency}` : ''}`
    ),
  get: (id: string) => request<LedgerEntry>(`/ledger/${id}`),
};

export interface ChainInfo {
  chain_id: number;
  name: string;
  native_symbol: string | null;
  logo_url: string | null;
}

export interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logo_url: string | null;
  supports_static_address: boolean;
}

export const discovery = {
  chains: () => request<ChainInfo[]>('/discovery/chains'),
  tokens: (chainId: number, staticOnly = true) =>
    request<TokenInfo[]>(`/discovery/tokens?chain_id=${chainId}&static_only=${staticOnly}`),
};

export const onramps = {
  list: (page = 1, filters: OrderFilters = {}) =>
    request<Paginated<OnrampInfo>>(`/onramps?${orderQuery(page, filters)}`),
  get: (id: string) => request<OnrampInfo>(`/onramps/${id}`),
};
